// src/components/ArtworkReadinessCard.jsx
// Art Asset Readiness (§7) — niveau de préparation patrimoniale d'UNE œuvre.
// Réutilise le moteur commun src/utils/readiness.js (§9.5). i18n FR / EN / DE.
import React from "react";
import { useTranslation } from "react-i18next";
import { computeArtworkReadiness, READINESS_DIMENSIONS } from "../utils/readiness";
import { generateArtAssetDossier } from "../utils/artAssetDossierPdf";

const tone = (pct) =>
  pct >= 80 ? "text-emerald-700" : pct >= 50 ? "text-amber-700" : "text-rose-700";

export default function ArtworkReadinessCard({ artwork, ownerName = "", className = "" }) {
  const { t } = useTranslation();
  if (!artwork) return null;
  const { dimensions, score } = computeArtworkReadiness(artwork);
  const missing = READINESS_DIMENSIONS.filter((d) => !dimensions[d.key]);

  return (
    <section className={`rounded-2xl border border-gray-200 bg-white p-5 ${className}`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">{t("readiness.cards.artwork.eyebrow")}</p>
          <p className="mt-0.5 text-sm text-gray-500">{t("readiness.cards.artwork.subtitle")}</p>
        </div>
        <div className={`text-3xl font-semibold ${tone(score)}`}>{score}%</div>
      </div>

      {/* Barre globale */}
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${score >= 80 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-rose-500"}`}
          style={{ width: `${score}%` }}
        />
      </div>

      {/* Checklist des 8 dimensions */}
      <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {READINESS_DIMENSIONS.map((d) => {
          const ok = dimensions[d.key];
          return (
            <li key={d.key} className="flex items-center gap-2 text-sm">
              <span className={ok ? "text-emerald-600" : "text-gray-300"}>{ok ? "✓" : "○"}</span>
              <span className={ok ? "text-gray-700" : "text-gray-500"}>{t(`readiness.dimensions.${d.key}`)}</span>
            </li>
          );
        })}
      </ul>

      {/* À compléter */}
      {missing.length > 0 && (
        <p className="mt-4 text-xs text-gray-500">
          {t("readiness.cards.artwork.missingPrefix")}{" "}
          {missing.map((d) => t(`readiness.dimensions.${d.key}`)).join(", ")}.
        </p>
      )}

      {/* Art Asset Dossier (§6) — dossier patrimonial / financement */}
      <button
        type="button"
        onClick={() => generateArtAssetDossier(artwork, { ownerName })}
        title={t("readiness.cards.artwork.dossierTitle")}
        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
      >
        {t("readiness.cards.artwork.dossierButton")}
      </button>
    </section>
  );
}
